import { useCallback, useEffect, useMemo, useState } from 'react';
import { NCFileVersion as NCFileVersionType } from '@interfaces/search';
import { toggleVersionInSelection } from '@components/ncFileVersions/helpers/toggleVersionInSelection';
import { showIdenticalVersionsAlert } from '@components/ncFileVersions/helpers/showIdenticalVersionsAlert';
import type { NCFileVersionPickerProps } from '@components/ncFileVersions/components/NCFileVersionPicker';

export interface UseManageNCFileVersionPickerParams
  extends Pick<NCFileVersionPickerProps, 'versions' | 'favoriteOrLatest' | 'initialSelection' | 'onCompare' | 'hide'> {}

export const useManageNCFileVersionPicker = ({ versions, favoriteOrLatest, initialSelection, onCompare, hide }: UseManageNCFileVersionPickerParams) => {
  const favorite = useMemo(() => versions.find((v) => v.isFavorite), [versions]);
  const otherVersions = useMemo(
    () => versions.filter((v) => v.ncFileVersionId !== favoriteOrLatest.ncFileVersionId),
    [versions, favoriteOrLatest.ncFileVersionId]
  );

  const [versionsToCompare, setVersionsToCompare] = useState<NCFileVersionType[]>(() => initialSelection ?? []);

  useEffect(() => {
    setVersionsToCompare((prev) => {
      if (!prev.length) {
        return prev;
      }

      let changed = false;
      const mapped = prev.map((version) => {
        const updated = versions.find((availableVersion) => availableVersion.ncFileVersionId === version.ncFileVersionId);
        if (updated && updated !== version) {
          changed = true;
          return updated;
        }
        return version;
      });

      return changed ? mapped : prev;
    });
  }, [versions]);

  const toggleVersionForComparison = useCallback((version: NCFileVersionType) => {
    setVersionsToCompare((prev) => toggleVersionInSelection(prev, version));
  }, []);

  const canCompare = versionsToCompare.length === 2;

  const handleCompare = useCallback(() => {
    if (!canCompare) {
      return;
    }

    const [first, second] = versionsToCompare;
    if (first.fileContentHash && second.fileContentHash && first.fileContentHash === second.fileContentHash) {
      showIdenticalVersionsAlert();
      return;
    }

    onCompare(versionsToCompare);
    hide?.();
  }, [canCompare, versionsToCompare, onCompare, hide]);

  const handleCancel = useCallback(() => hide?.(), [hide]);

  return {
    favorite,
    otherVersions,
    versionsToCompare,
    toggleVersionForComparison,
    canCompare,
    handleCompare,
    handleCancel,
  };
};
